/* Header drawer: a stack of cards below the bar.
   The bar keeps its shape and the menu arrives as separate floating cards
   beneath it (scuffers.com pattern). It replaced an expand-in-place card on
   2026-08-04; #page-menu is now a SIBLING of #site-control, not a child,
   because the card is overflow:hidden with its own surface.

   The old full-screen modal is bypassed rather than modified: the hamburger's
   trigger attribute was renamed from data-modal-nav-toggle to
   data-header-drawer-toggle, so theme.js's handler no longer matches it. */
(function () {
  'use strict';

  function init() {
    var card = document.getElementById('site-control');
    var toggle = card && card.querySelector('[data-header-drawer-toggle]');
    var drawer = document.getElementById('page-menu');
    if (!card || !toggle || !drawer) return;

    var collapseTimer = 0;
    function setOpen(open) {
      window.clearTimeout(collapseTimer);
      card.classList.toggle('drawer-open', open);
      /* Drives the page blur, the card stagger and the scroll lock in
         styles.css.liquid. Set on the root element, not the card, because
         all three apply outside the card's subtree. */
      document.documentElement.classList.toggle('drawer-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (!open) {
        var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        collapseTimer = window.setTimeout(function () {
          if (!card.classList.contains('drawer-open')) collapseAll(drawer);
        }, reducedMotion ? 0 : 320);
        /* Return focus to the control that opened it, or the user is left
           at the top of a page they can no longer see. */
        toggle.focus({ preventScroll: true });
      }
    }

    /* The drawer is a modal surface over a blurred page — tab must not walk
       off into content the user cannot see or read. */
    document.addEventListener('keydown', function (e) {
      if (!card.classList.contains('drawer-open')) return;
      trapFocus(e, drawer, toggle);
    });

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      setOpen(!card.classList.contains('drawer-open'));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && card.classList.contains('drawer-open')) setOpen(false);
    });

    document.addEventListener('click', function (e) {
      if (!card.classList.contains('drawer-open')) return;
      /* The drawer is no longer inside the card, so testing the card alone
         would treat every click on the menu itself as an outside click and
         close it immediately. Both surfaces count as "inside". */
      if (!card.contains(e.target) && !drawer.contains(e.target)) setOpen(false);
    });

    var desktopClose = drawer.querySelector('[data-desktop-drawer-close]');
    if (desktopClose) desktopClose.addEventListener('click', function () { setOpen(false); });
    initDesktopHeader(card);
    initAccordion(drawer);
    initPromo(drawer);
    initHideOnScroll(card);
    initStripStuck();
  }


  function initDesktopHeader(card) {
    if (card.dataset.desktopHeaderBound === 'true') return;
    card.dataset.desktopHeaderBound = 'true';
    var desktop = window.matchMedia('(min-width: 992px)');
    var frame = 0;
    var lastWidth = -1;
    var compact = window.pageYOffset > 100;
    var lastState = '';
    function number(name, fallback) {
      return parseFloat(card.style.getPropertyValue(name)) || fallback;
    }
    // Read settings only when viewport width changes, never per scroll frame.
    function sizeLogo() {
      var width = window.innerWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      var expanded, small;
      if (desktop.matches) {
        expanded = Math.min(number('--sc-logo-expanded', 170), width * .2);
        small = number('--sc-logo-compact', 110);
      } else {
        expanded = Math.min(number('--sc-mobile-logo-expanded', 160), Math.max(1, width - 192));
        small = Math.min(number('--sc-mobile-logo-compact', 140), expanded);
      }
      card.style.setProperty('--sc-motion-logo-width', expanded + 'px');
      card.style.setProperty('--sc-motion-logo-scale', String(small / expanded));
      card.classList.add('sc-motion-ready');
      document.documentElement.classList.add('sc-header-motion');
    }
    function update() {
      frame = 0;
      sizeLogo();
      var y = Math.max(0, window.pageYOffset);
      // A small dead band avoids repeated reversals around a single threshold.
      if (y > 112) compact = true;
      else if (y < 80) compact = false;
      var state = (desktop.matches ? 'desktop-' : 'mobile-') + compact;
      if (state === lastState) return;
      lastState = state;
      card.classList.toggle('sc-header-compact', desktop.matches && compact);
      card.classList.toggle('sc-mobile-header-compact', !desktop.matches && compact);
    }
    function schedule() { if (!frame) frame = window.requestAnimationFrame(update); }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    if (desktop.addEventListener) desktop.addEventListener('change', schedule);
    else desktop.addListener(schedule);
    update();
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), ' +
                  'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Cycle Tab within [toggle, ...drawer contents]. The toggle is included
     because it is the close control — trapping the user inside the menu with
     no keyboard route out would be worse than not trapping at all. */
  function trapFocus(e, drawer, toggle) {
    if (e.key !== 'Tab') return;

    var items = [toggle].concat(
      Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE))
    ).filter(function (el) {
      /* offsetParent is null for display:none. Collapsed accordion sublists
         are NOT caught by that — they are max-height:0, which clips without
         removing from layout, so their links stay tabbable. They carry
         visibility:hidden in the CSS for exactly this reason; browsers drop
         visibility:hidden subtrees from the tab order natively, so the trap
         does not have to special-case them. */
      return el && el.offsetParent !== null;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* Promo card fold. State lives on the card so the CSS can drive the body
     collapse and the chevron flip from one class. */
  function initPromo(drawer) {
    var toggle = drawer.querySelector('[data-promo-toggle]');
    if (!toggle) return;
    var card = toggle.closest('.drawer-card--promo');
    if (!card) return;

    toggle.addEventListener('click', function () {
      var collapsed = card.classList.toggle('is-collapsed');
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
  }

  /* Accordion, replacing theme.js's drill-down.

     The drill-down swapped the whole menu for a child screen you had to back
     out of. Merchant feedback asked for the scuffers pattern: tap a parent and
     its children unfold underneath it, indented and smaller.

     Requires nav-uses-modal to be absent from #page-menu — see the comment in
     sections/header.liquid for the three theme behaviours that switch off.

     theme.js adds .has-children to any li containing a ul (theme.js:6170,
     ungated), but that runs on section load, which may be after this. Detect
     children directly rather than depending on the ordering. */
  function initAccordion(drawer) {
    /* Every .main-nav, not the first: the promo card renders its own menu
       above the main one, so querySelector would bind the accordion to the
       promo card and leave the actual menu inert. */
    drawer.querySelectorAll('.main-nav').forEach(bindNav);
  }

  function bindNav(nav) {
    nav.querySelectorAll(':scope > ul > li').forEach(function (li) {
      var sub = li.querySelector(':scope > ul');
      var link = li.querySelector(':scope > a');
      if (!sub || !link) return;

      li.classList.add('has-children');
      link.setAttribute('aria-expanded', 'false');

      link.addEventListener('click', function (e) {
        /* Without this the parent link navigates to its own collection page,
           since nothing is intercepting it once the drill-down is unbound. */
        e.preventDefault();
        var willOpen = !li.classList.contains('is-open');

        /* One open at a time: several long collection lists at once outgrow
           the card and push the footer pills out of reach. */
        nav.querySelectorAll(':scope > ul > li.is-open').forEach(function (other) {
          if (other !== li) collapse(other);
        });

        if (willOpen) {
          li.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
          /* Measured, not guessed — the CSS carries only a blanket fallback,
             which would ease the transition against the wrong distance. */
          sub.style.maxHeight = sub.scrollHeight + 'px';
        } else {
          collapse(li);
        }
      });
    });
  }

  function collapse(li) {
    var sub = li.querySelector(':scope > ul');
    var link = li.querySelector(':scope > a');
    li.classList.remove('is-open');
    if (link) link.setAttribute('aria-expanded', 'false');
    if (sub) sub.style.maxHeight = '';
  }

  /* Reset on close so reopening always starts at the top level. */
  function collapseAll(drawer) {
    drawer.querySelectorAll('.main-nav > ul > li.is-open').forEach(collapse);
  }

  /* Hide-on-scroll, matching emestudios.com.

     Measured on the live site: collection/list pages slide the header fully
     off-screen once you scroll DOWN past ~240px, and reveal it again on the
     FIRST upward scroll at any depth. The homepage and product pages never
     hide — the header stays pinned so the cart is always reachable.

     The threshold and direction rule were measured by stepping 60px at a time;
     jump-testing gives inconsistent results because the handler is
     direction-based, not position-based. */
  var HIDE_AFTER = 240;

  function initHideOnScroll(card) {
    var body = document.body;

    /* The header section has exposed this all along as "Scroll behavior", and
       it is set to "Sticks on scroll" — but this handler hid the card anyway.
       The setting already reaches the DOM as data-positioning on the card and
       simply had no reader, so the editor said pinned and the page said
       otherwise.

       Honour it: only "Hides on scroll down" (peek) hides. The product page
       stays pinned regardless, so the cart is always one tap away while
       viewing an item — that part matches EME. */
    if (card.getAttribute('data-positioning') !== 'peek') return;
    if (body.classList.contains('template-product')) return;

    var root = document.documentElement;
    var last = window.pageYOffset;
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.pageYOffset;
      var goingDown = y > last;
      last = y;

      // Never hide the header while the menu is open.
      if (card.classList.contains('drawer-open')) {
        root.classList.remove('header-hidden');
        return;
      }

      if (goingDown && y > HIDE_AFTER) {
        root.classList.add('header-hidden');
      } else if (!goingDown) {
        root.classList.remove('header-hidden');
      }
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
  }

  /* The homepage tab strip lives mid-page and only meets the card once it
     sticks. Joining them unconditionally would leave the card square-bottomed
     with nothing beneath it while the strip is still further down the page, so
     the joined corners are applied only while it is actually stuck. */
  function initStripStuck() {
    var strip = document.querySelector('.tabbed-collection__header');
    if (!strip) return;

    var root = document.documentElement;
    var ticking = false;

    function update() {
      ticking = false;
      var stickyTop = parseFloat(getComputedStyle(strip).top);
      if (isNaN(stickyTop)) return;
      var stuck = Math.round(strip.getBoundingClientRect().top) <= Math.round(stickyTop) + 1;
      root.classList.toggle('strip-stuck', stuck);
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();